import _extends from "@babel/runtime/helpers/extends";
import _typeof from "@babel/runtime/helpers/typeof";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _elementMappings;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useContext } from 'react';
import { FormattedDate } from 'react-intl-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { ElementName, IconType, SmartLinkInternalTheme } from '../../../../constants';
import { messages } from '../../../../messages';
import { FlexibleUiContext } from '../../../../state/flexible-ui-context';
import { isProfileType } from '../../../../utils';
import AppliedToComponentsCount from './applied-to-components-count';
import AtlaskitBadge from './atlaskit-badge';
import AvatarGroup from './avatar-group';
import Badge from './badge';
import DateTime from './date-time';
import Icon from './icon';
import Link from './link';
import Lozenge from './lozenge';
import Media from './media';
import Text from './text';
var elementMappings = (_elementMappings = {}, _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_elementMappings, ElementName.AppliedToComponentsCount, {
  component: AppliedToComponentsCount,
  props: {
    icon: IconType.Component
  }
}), ElementName.AttachmentCount, {
  component: Badge,
  props: {
    icon: IconType.Attachment
  }
}), ElementName.AuthorGroup, {
  component: AvatarGroup
}), ElementName.ChecklistProgress, {
  component: Badge,
  props: {
    icon: IconType.CheckItem
  }
}), ElementName.CollaboratorGroup, {
  component: AvatarGroup
}), ElementName.CommentCount, {
  component: Badge,
  props: {
    icon: IconType.Comment
  }
}), ElementName.ViewCount, {
  component: Badge,
  props: {
    icon: IconType.View
  }
}), ElementName.ReactCount, {
  component: Badge,
  props: {
    icon: IconType.React
  }
}), ElementName.VoteCount, {
  component: Badge,
  props: {
    icon: IconType.Vote
  }
}), ElementName.CreatedBy, {
  component: Text
}), _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_elementMappings, ElementName.OwnedBy, {
  component: Text
}), ElementName.AssignedTo, {
  component: Text
}), ElementName.AssignedToGroup, {
  component: AvatarGroup
}), ElementName.OwnedByGroup, {
  component: AvatarGroup
}), ElementName.CreatedOn, {
  component: DateTime
}), ElementName.DueOn, {
  component: Lozenge
}), ElementName.LatestCommit, {
  component: Badge,
  props: {
    icon: IconType.Commit
  }
}), ElementName.LinkIcon, {
  component: Icon
}), ElementName.ModifiedBy, {
  component: Text
}), ElementName.ModifiedOn, {
  component: DateTime
}), _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_elementMappings, ElementName.Preview, {
  component: Media
}), ElementName.Priority, {
  component: Badge
}), ElementName.ProgrammingLanguage, {
  component: Badge,
  props: {
    icon: IconType.ProgrammingLanguage
  }
}), ElementName.Provider, {
  component: Badge
}), ElementName.ReadTime, {
  component: Text
}), ElementName.SentOn, {
  component: DateTime
}), ElementName.SourceBranch, {
  component: Text
}), ElementName.State, {
  component: Lozenge
}), ElementName.SubscriberCount, {
  component: Badge,
  props: {
    icon: IconType.Subscriber
  }
}), ElementName.SubTasksProgress, {
  component: Badge,
  props: {
    icon: IconType.SubTasksProgress
  }
}), _defineProperty(_defineProperty(_defineProperty(_defineProperty(_elementMappings, ElementName.StoryPoints, {
  component: AtlaskitBadge
}), ElementName.TargetBranch, {
  component: Text
}), ElementName.Title, {
  component: Link
}), ElementName.Location, {
  component: Link,
  props: {
    theme: SmartLinkInternalTheme.Grey
  }
}));
var getContextKey = function getContextKey(name) {
  // Attempt to predict context prop name in advance to reduce the amount of
  // code run during runtime
  return name.length > 0 ? name.charAt(0).toLowerCase() + name.slice(1) : undefined;
};
var getData = function getData(elementName, contextKey, context) {
  if (!context) {
    return undefined;
  }
  var data = context[contextKey];
  switch (elementName) {
    case ElementName.AssignedToGroup:
    case ElementName.AuthorGroup:
    case ElementName.CollaboratorGroup:
    case ElementName.OwnedByGroup:
      var AvatarGroupsWithFallback = [ElementName.AssignedToGroup];
      var showFallbackAvatar = AvatarGroupsWithFallback.includes(elementName);
      return toAvatarGroupProps(data, showFallbackAvatar);
    case ElementName.AppliedToComponentsCount:
    case ElementName.AttachmentCount:
    case ElementName.ChecklistProgress:
    case ElementName.CommentCount:
    case ElementName.ViewCount:
    case ElementName.ReactCount:
    case ElementName.VoteCount:
    case ElementName.ProgrammingLanguage:
    case ElementName.SubscriberCount:
    case ElementName.LatestCommit:
    case ElementName.SubTasksProgress:
      return toBadgeProps(data);
    case ElementName.StoryPoints:
      return toAtlaskitBadgeProps(data);
    case ElementName.CreatedBy:
      return toFormattedTextProps(messages.created_by, context.createdBy);
    case ElementName.AssignedTo:
      return toFormattedTextProps(messages.assigned_to, context.assignedTo);
    case ElementName.OwnedBy:
      return toFormattedTextProps(messages.owned_by, context.ownedBy);
    case ElementName.CreatedOn:
      return toDateTimeProps('created', context.createdOn);
    case ElementName.DueOn:
      return toDateLozengeProps(context.dueOn);
    case ElementName.ModifiedBy:
      return toFormattedTextProps(messages.modified_by, context.modifiedBy);
    case ElementName.ModifiedOn:
      return toDateTimeProps('modified', context.modifiedOn);
    case ElementName.SentOn:
      return toDateTimeProps('sent', context.sentOn);
    case ElementName.ReadTime:
      return toFormattedTextProps(messages.read_time, data);
    case ElementName.SourceBranch:
    case ElementName.TargetBranch:
      return toTextProps(data);
    case ElementName.Title:
      return toLinkProps(context.title, context.url);
    case ElementName.LinkIcon:
      if (fg('platform-linking-visual-refresh-v2')) {
        return toLinkIconProps(data, context.type);
      }
      return _typeof(data) === 'object' ? data : undefined;
    default:
      return _typeof(data) === 'object' ? data : undefined;
  }
};
var toAvatarGroupProps = function toAvatarGroupProps(items, showFallbackAvatar) {
  return items ? {
    items: items
  } : showFallbackAvatar ? {
    items: []
  } : undefined;
};
var toBadgeProps = function toBadgeProps(label) {
  return label ? {
    label: label
  } : undefined;
};
var toAtlaskitBadgeProps = function toAtlaskitBadgeProps(value) {
  return value ? {
    value: value
  } : undefined;
};
var toDateLozengeProps = function toDateLozengeProps(dateString) {
  if (dateString) {
    var text = Date.parse(dateString) ? /*#__PURE__*/React.createElement(FormattedDate, {
      value: new Date(dateString),
      year: "numeric",
      month: "short",
      day: "numeric",
      formatMatcher: "best fit"
    }) : dateString;
    return {
      text: text
    };
  }
};
var toDateTimeProps = function toDateTimeProps(type, dateString) {
  return dateString ? {
    date: new Date(dateString),
    type: type
  } : undefined;
};
var toFormattedTextProps = function toFormattedTextProps(descriptor, context) {
  return context ? {
    message: {
      descriptor: descriptor,
      values: {
        context: context
      }
    }
  } : undefined;
};
var toLinkProps = function toLinkProps(text, url) {
  return text ? {
    text: text,
    url: url
  } : undefined;
};
var toTextProps = function toTextProps(content) {
  return content ? {
    content: content
  } : undefined;
};
var toLinkIconProps = function toLinkIconProps(data, type) {
  var isDataLinkIcon = function isDataLinkIcon(_data) {
    return _typeof(_data) === 'object' && _data !== null && ('icon' in _data || 'url' in _data);
  };
  if (!isDataLinkIcon(data)) {
    return _typeof(data) === 'object' ? data : undefined;
  }
  var isImageRound = isProfileType(type);
  return _objectSpread(_objectSpread({}, data), {}, {
    appearance: isImageRound ? 'round' : 'square'
  });
};
export var createElement = function createElement(name) {
  var _ref = elementMappings[name] || {},
    BaseElement = _ref.component,
    props = _ref.props;
  var contextKey = getContextKey(name);
  if (!BaseElement) {
    throw Error("Element ".concat(name, " does not exist."));
  }
  return function (overrides) {
    var context = useContext(FlexibleUiContext);
    var data = getData(name, contextKey, context);
    return data ? /*#__PURE__*/React.createElement(BaseElement, _extends({}, props, data, overrides, {
      name: name
    })) : null;
  };
};